import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useRouteError } from 'react-router';
import { Button } from '@/components/ui';
import { Logo } from '@/components/Logo';

/** A crash must never take the timer with it: the local data is intact, so offer a reload and a way out. */
export function CrashScreen({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="min-h-dvh grid place-items-center bg-canvas text-ink px-4">
      <div className="max-w-[420px] text-center grid gap-4 justify-items-center">
        <Logo size={32} />
        <h1 className="text-section m-0">Something broke on this screen</h1>
        <p className="text-body-sm text-ink-2 m-0">Your sessions are safe on this device - nothing was lost. Reloading usually clears it.</p>
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => window.location.reload()}>Reload</Button>
          <Button variant="secondary" onClick={() => { window.location.href = '/timer'; }}>Back to the timer</Button>
        </div>
        <p className="text-inline-meta text-ink-3 m-0 font-mono break-all">{message}</p>
      </div>
    </div>
  );
}

/** React Router catches render errors inside a route itself; without this it shows its own developer page. */
export function RouteError() {
  const error = useRouteError();
  console.error('[timato] route failed', error);
  return <CrashScreen error={error} />;
}

/** Outside the router (the router itself, providers): the class boundary React still needs for that. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('[timato] render failed', error, info.componentStack); }
  render() { return this.state.error ? <CrashScreen error={this.state.error} /> : this.props.children; }
}
